import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Scatter, ZAxis, Rectangle
} from 'recharts';
import { FaUser, FaList, FaBrain } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateExpectedPercentage, calculatePreviousYearPercentage, deriveAnchoredAge } from '../utils/levelCalculations';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface DimensionSummaryProps {
  student: Student;
  dimensionId: number;
}

const COLORS = {
  previousYear: '#6B7280',
  achieved: '#4A90E2',
  expected: '#F5A623',
  headerBackground: '#1F2937', // Color de fondo para encabezados
  headerText: '#FFFFFF', // Color de texto para encabezados
};

// Habilidades de Autoconciencia y Voluntad (hardcodeadas)
const autoconcienciaSkills: Skill[] = [
      {
        "id": 1,
        "name": "Nombrar emociones básicas",
        "levels": [
          "Nombra emociones básicas (alegría, tristeza, enojo) con apoyo.",
          "En proceso",
          "Reconoce emociones propias en diferentes situaciones cotidianas.",
          "En proceso",
          "Reconoce emociones como vergüenza, frustración o entusiasmo.",
          "En proceso",
          "Distingue emociones más complejas e identifica qué las provoca.",
          "En proceso",
          "Reflexiona sobre sus emociones, su origen y cómo manejarlas."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 2,
        "name": "Identificar emociones en otros",
        "levels": [
          "Identifica emociones básicas en otros con apoyo del adulto.",
          "En proceso",
          "Identifica cómo se sienten otros al observar gestos o escuchar lo que dicen.",
          "En proceso",
          "Interpreta emociones de sus pares y reacciona con empatía.",
          "En proceso",
          "Se pone en el lugar del otro, comprende emociones ajenas y actúa con empatía.",
          "En proceso",
          "Reconoce emociones complejas en otros y adapta su conducta de forma empática."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 3,
        "name": "Expresar y autorregular emociones",
        "levels": [
          "Expresa emociones con gestos o llanto y requiere ayuda para autorregularse.",
          "En proceso",
          "Usa palabras simples para decir cómo se siente. Requiere de la ayuda del adulto.",
          "En proceso",
          "Con o sin apoyo del adulto, regula su expresión emocional en contextos conocidos.",
          "En proceso",
          "Adapta la expresión emocional según el contexto.",
          "En proceso",
          "Expresa emociones de forma asertiva, controlada y consciente."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 4,
        "name": "Reconocimiento de rasgos e identidad",
        "levels": [
          "Reconoce rasgos simples propios (nombre, edad) y muestra curiosidad por otros.",
          "En proceso",
          "Identifica diferencias físicas y de gustos en sus pares.",
          "En proceso",
          "Acepta diferencias personales y sociales en el grupo.",
          "En proceso",
          "Describe aspectos sociales, físicos y emocionales de su identidad.",
          "En proceso",
          "Integra distintas dimensiones de su identidad y promueve la inclusión."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 5,
        "name": "Autoevaluación de gustos y fortalezas",
        "levels": [
          "Dice qué le gusta o qué le sale bien.",
          "En proceso",
          "Reconoce cosas que sabe hacer y otras que necesita mejorar.",
          "En proceso",
          "Reconoce y habla de sus fortalezas y busca mejorar en otras áreas.",
          "En proceso",
          "Evalúa sus capacidades y se plantea desafíos personales.",
          "En proceso",
          "Se propone metas personales y reconoce logros con realismo."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 6,
        "name": "Identificación y prevención de riesgos",
        "levels": [
          "Identifica riesgos evidentes (fuego, caídas) con ayuda.",
          "En proceso",
          "Sigue normas básicas de autocuidado con supervisión.",
          "En proceso",
          "Identifica riesgos escolares y propone acciones de cuidado.",
          "En proceso",
          "Reconoce riesgos sociales (acoso, presión) y actúa para prevenirlos.",
          "En proceso",
          "Actúa proactivamente para prevenir riesgos personales y colectivos."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 7,
        "name": "Resolución de conflictos y reparación",
        "levels": [
          "Reacciona ante conflictos con apoyo del adulto (pide disculpas).",
          "En proceso",
          "Relaciona sus acciones con consecuencias y repara si se le indica.",
          "En proceso",
          "Reconoce su parte en conflictos y busca reparar con guía.",
          "En proceso",
          "Asume su responsabilidad en conflictos y repara proactivamente.",
          "En proceso",
          "Repara el daño causado con empatía y busca prevenir futuros conflictos."
        ],
        "selectedLevelIndex": null
      },
      {
        "id": 8,
        "name": "Responsabilidad y autonomía en tareas",
        "levels": [
          "Asume pequeñas tareas cuando se le asignan y con acompañamiento.",
          "En proceso",
          "Acepta encargos simples y los realiza con recordatorios.",
          "En proceso",
          "Cumple compromisos individuales con creciente autonomía.",
          "En proceso",
          "Asume responsabilidades en el aula o grupo, y responde por ellas.",
          "En proceso",
          "Se compromete con tareas grupales e individuales, asumiendo consecuencias si no cumple."
        ],
        "selectedLevelIndex": null
      }
    ];

// Definición de la dimensión con datos hardcodeados
export const autoconcienciaVoluntadDimension: Dimension = {
  id: 1,
  name: "Autoconciencia y Voluntad",
  skills: autoconcienciaSkills // Datos hardcodeados permanentes
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill } = props;
  return (
    <Rectangle
      x={cx - 1}
      y={cy - 25}
      width={4}
      height={40}
      fill={fill}
    />
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow">
        <p className="label font-bold">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${index}`} style={{ color: entry.fill }}>
            {`${entry.name}: ${entry.value.toFixed(2)}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

const AutoconcienciaVoluntad: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel,
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [showAllYears, setShowAllYears] = useState(true);
  
  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  // Función para obtener el nivel seleccionado de una habilidad específica de un estudiante
  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === autoconcienciaVoluntadDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  // Función para filtrar estudiantes
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");
  
  // Función para obtener el color según el porcentaje de completado
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'bg-indigo-500 text-white border-indigo-600 shadow-md transform scale-105';
    }
    
    if (percentage >= 75) {
      return 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400';
    } else if (percentage >= 50) {
      return 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400';
    } else if (percentage >= 25) {
      return 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400';
    } else {
      return 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400';
    }
  };

  // Función para obtener el color del texto del porcentaje
  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'text-white opacity-75';
    }
    
    if (percentage >= 75) {
      return 'text-green-600 opacity-90';
    } else if (percentage >= 50) {
      return 'text-blue-600 opacity-90';
    } else if (percentage >= 25) {
      return 'text-yellow-600 opacity-90';
    } else {
      return 'text-red-600 opacity-90';
    }
  };
  
  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };
  
  // Filtrar estudiantes basados en el término de búsqueda y ordenarlos
  const filteredStudents = useMemo(() => {
    const filtered = students.filter(student => 
      selectedCourse ? student.course === selectedCourse : true
    );
    
    let sorted = [...filtered];
    
    // Ordenar según el criterio seleccionado
    if (sortBy === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "lastName") {
      sorted.sort((a, b) => {
        const lastNameA = getFirstLastName(a.name);
        const lastNameB = getFirstLastName(b.name);
        return lastNameA.localeCompare(lastNameB);
      });
    } else if (sortBy === "course") {
      sorted.sort((a, b) => a.course.localeCompare(b.course));
    }
    
    if (!searchTerm.trim()) return sorted;
    
    return sorted.filter(student => 
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, selectedCourse, searchTerm, sortBy]);

  // Modificamos el useEffect para manejar la selección por defecto
  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId) {
      const firstSkill = autoconcienciaVoluntadDimension.skills[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect, selectedSkillId]);

  // Nuevo useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    // Inicializar los niveles previamente guardados en la base de datos
    const initialLevels: {[key: string]: number} = {};
    
    students.forEach(student => {
      student.skillLevels.forEach(skillLevel => {
        if (skillLevel.dimensionId === autoconcienciaVoluntadDimension.id && skillLevel.level !== null) {
          initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
        }
      });
    });
    
    // Solo actualizar si hay datos que inicializar
    if (Object.keys(initialLevels).length > 0) {
      setPreviouslySelectedLevels(initialLevels);
    }
  }, [students]); // Solo se ejecuta cuando cambia la lista de estudiantes

  const SkillsChart = useCallback(({ student }: { student: Student }) => {
    // Calcular la edad del estudiante
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    // Preparar los datos usando getSkillLevel
    const data = autoconcienciaVoluntadDimension.skills.map(skill => ({
      name: skill.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved: getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id) === null 
        ? 0 
        : (getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id)! + 1) * 20,
      expectedLevel: calculateExpectedLevel(age),
    }));

    const CustomTooltip = ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-2 border rounded shadow">
            <p className="label font-bold">{`${label}`}</p>
            {payload.map((entry: any, index: number) => (
              <p key={`item-${index}`} style={{ color: entry.fill }}>
                {`${entry.name}: ${entry.value.toFixed(2)}%`}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    const maxSkillNameLength = Math.max(...data.map(d => d.name.length));
    const yAxisWidth = Math.min(200, maxSkillNameLength * 6);

    return (
      <div className="bg-white rounded-lg shadow-md mb-4">
        <h3 className="text-xl font-bold bg-gray-800 text-white p-3 rounded-t-lg">Gráfico de {autoconcienciaVoluntadDimension.name}</h3>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={Math.max(400, data.length * 40)}>
            <ComposedChart
              layout="vertical"
              data={data}
              margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                type="number" 
                domain={[0, 100]}
                ticks={[20, 40, 60, 80, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis dataKey="name" type="category" width={yAxisWidth} tick={{ fontSize: 12 }} />
              <ZAxis range={[400, 400]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="percentageAchieved" fill={COLORS.achieved} name="Nivel habilidad logrado" />
              <Scatter dataKey="previousYearLevel" fill={COLORS.previousYear} name="Nivel habilidad logrado año previo" shape="square" legendType="square" />
              <Scatter dataKey="expectedLevel" fill={COLORS.expected} name="Nivel habilidad esperado" shape="diamond" legendType="diamond" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }, []);

  const DimensionSummary = useCallback(({ student, dimensionId }: DimensionSummaryProps) => {
    // Calcular la edad del estudiante
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    // Calcular el porcentaje logrado usando skillLevels
    const percentageAchieved = autoconcienciaVoluntadDimension.skills.reduce((sum, skill) => {
      const level = getSkillLevel(student, dimensionId, skill.id);
      // ✅ CORRECCIÓN: Añadir año base para alinear con edad real seleccionada
      return sum + (level === null ? 0 : (level + 1) * 20 + 20);
    }, 0) / autoconcienciaVoluntadDimension.skills.length;

    const data = [{
      name: autoconcienciaVoluntadDimension.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved,
      expectedLevel: calculateExpectedLevel(age),
    }];

    const yAxisWidth = 200;
    const barSize = 40;

    return (
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Resumen de {autoconcienciaVoluntadDimension.name}</h3>
        <div className="w-full" style={{ height: '200px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              layout="vertical"
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 110]}
                ticks={[20, 60, 100]}
                tickFormatter={(value) => {
                  const ageMap: { [key: number]: string } = {
                    20: 'Hacia los 4 años',
                    60: 'Hacia los 6 años',
                    100: 'Hacia los 8 años'
                  };
                  return ageMap[value] || '';
                }}
                tick={{ fontSize: 14, fontWeight: 'bold' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={yAxisWidth}
                tick={({ x, y, payload }) => (
                  <text
                    x={x}
                    y={y}
                    dy={5}
                    textAnchor="end"
                    fill="#333"
                    fontSize={14}
                    fontWeight="bold"
                  >
                    {payload.value}
                  </text>
                )}
                axisLine={false}
                tickLine={false}
              />
              <ZAxis range={[200, 200]} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="percentageAchieved" 
                fill={COLORS.achieved} 
                name="Nivel habilidad logrado" 
                barSize={barSize}
              />
              <Bar
                dataKey="expectedLevel"
                fill={COLORS.expected}
                name="Nivel esperado"
                barSize={4}
                shape={<CustomBar />}
              />
              <Scatter 
                dataKey="previousYearLevel" 
                fill={COLORS.previousYear} 
                name="Nivel año anterior" 
                shape={<CustomVerticalRectangle />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }, []);

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
    // Guardar el nivel actual antes de cambiarlo
    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, autoconcienciaVoluntadDimension.id, skillId);

      // Solo actualizamos previouslySelectedLevels si no existe ya un valor para este estudiante y skill
      // y si el nivel actual no es null (es decir, hay un valor guardado en la BD)
      if (currentLevel !== null && !previouslySelectedLevels?.[`${studentId}-${skillId}`]) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }
    }

    // Si levelIndex es -1, lo tratamos como null para resetear el nivel
    const finalLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, autoconcienciaVoluntadDimension.id, skillId, finalLevelIndex);
    
    // No eliminamos valores de previouslySelectedLevels ya que queremos mantener
    // la referencia a los valores guardados en la base de datos
  }, [handleLevelChange, students, getSkillLevel]);

  const selectColumnForAllStudents = (levelIndex: number, students: Student[], selectedSkillId: number, handleLevelChange: Function) => {
    students.forEach(student => {
      const currentLevel = student.skillLevels.find(
        sl => sl.dimensionId === autoconcienciaVoluntadDimension.id && sl.skillId === selectedSkillId
      )?.level;

      if (currentLevel === levelIndex) {
        // Si el nivel ya está seleccionado, lo desmarcamos
        handleLevelChange(student.id as any, autoconcienciaVoluntadDimension.id, selectedSkillId, null);
      } else {
        // Si no está seleccionado, lo marcamos
        handleLevelChange(student.id as any, autoconcienciaVoluntadDimension.id, selectedSkillId, levelIndex);
      }
    });
  };

  const selectColumnForAllSkills = (levelIndex: number, studentId: number | string, handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, level: number | null) => void) => {
    autoconcienciaVoluntadDimension.skills.forEach(skill => {
      const currentLevel = students.find(s => s.id === studentId)?.skillLevels.find(
        sl => sl.dimensionId === autoconcienciaVoluntadDimension.id && sl.skillId === skill.id
      )?.level;

      if (currentLevel === levelIndex) {
        // Si el nivel ya está seleccionado, lo desmarcamos
        handleLevelChange(studentId, autoconcienciaVoluntadDimension.id, skill.id, null);
      } else {
        // Si no está seleccionado, lo marcamos
        handleLevelChange(studentId, autoconcienciaVoluntadDimension.id, skill.id, levelIndex);
      }
    });
  };

  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
      if (!student) return null;

      // Calcular la edad del estudiante
      const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

      const expectedLevel = getExpectedLevel(age);

      // Generar array completo de años (4-12)
      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      // Filtrar años según la configuración
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        // Mostrar edad ±2 años (mínimo 3 años, máximo 12)
        const studentAge = age;
        const minAge = Math.max(4, studentAge - 2);
        const maxAge = Math.min(12, studentAge + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
        const match = year.match(/\d+/);
        const yearNum = match ? parseInt(match[0]) : 4;
        const calculatedIndex = yearNum - 4;
        // Asegurar que nunca sea NaN y usar fallback con index
        return isNaN(calculatedIndex) ? index : calculatedIndex;
      });

      return (
        <div key={student.id} className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              {/* Indicador de progreso - Nuevo */}
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                        const progress = calculateFormProgress(
                          autoconcienciaVoluntadDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        const progressPercent = progress.percentage;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const progress = calculateFormProgress(
                          autoconcienciaVoluntadDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                      })()}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                          autoconcienciaVoluntadDimension.skills.forEach(skill => {
                            // Pasar un valor para level que será interpretado como null
                            handleLocalLevelChange(student.id as any, skill.id, -1);
                          });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Reiniciar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                  {visibleYears.map((ageGroup, index) => {
                    const actualIndex = visibleIndices[index]; // Obtener el índice real del nivel
                    return (
                      <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="mb-1">{ageGroup}</span>
                          <button
                            onClick={() => selectColumnForAllSkills(actualIndex, student.id as any, handleLevelChange)}
                            className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                          >
                            Seleccionar todo
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                {autoconcienciaVoluntadDimension.skills.map((skill: Skill, skillIndex: number) => {
                  const selectedLevel = getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id);
                  const skillIsNotEvaluated = isSkillNotEvaluated(student, autoconcienciaVoluntadDimension.id, skill.id);
                  
                  const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                  
                  return (
                        <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                              <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                                {skillIndex + 1}
                              </span>
                              <div className="flex-1">
                                {skill.name}
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, autoconcienciaVoluntadDimension.id, skill.id, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                      {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                        const level = skill.levels[levelIndex];
                        if (!level) return null; // Si no existe el nivel para este índice, no mostrar nada
                            // Determinar si este nivel "no aplica" basado en el texto
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                        
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              // Si es "No aplica", verificar si está seleccionado
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              // Mostrar nivel previamente seleccionado
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (levelIndex === previousYearLevel) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (levelIndex === expectedLevel) {
                              // Si es esperado, aplicar borde amarillo
                              if (levelIndex !== previousYearLevel) { // Evitar doble borde si es ambos
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }

                        return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                            <button
                              onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                              className={buttonClass}
                              style={extraStyles}
                              title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${levelIndex === previousYearLevel ? ' (Nivel año anterior)' : ''}${levelIndex === expectedLevel ? ' (Nivel esperado)' : ''}`}
                            >
                                  <span className="line-clamp-4">{level}</span>
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </div>
              
              {/* Controles de navegación rápida - Nuevo */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [selectedStudentIds, students, handleLocalLevelChange, getExpectedLevel, getPreviousYearLevel, showAllYears]);

  const renderSkillView = useMemo(() => {
    return () => {
      const skill = autoconcienciaVoluntadDimension.skills.find(s => s.id === selectedSkillId);
      if (!skill) return null;

      // Generar array completo de años (4-12) - igual que en renderStudentView
      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      // Filtrar años según la configuración - igual que en renderStudentView
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        // Para vista por habilidad, mostrar un rango fijo de ±2 años
        // calculado basado en la edad promedio de los estudiantes filtrados
        const averageAge = filteredStudents.length > 0 ? 
          filteredStudents.reduce((sum, student) => {
            const a = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            return sum + a;
          }, 0) / filteredStudents.length : 6;
        
        const minAge = Math.max(4, Math.floor(averageAge) - 2);
        const maxAge = Math.min(12, Math.floor(averageAge) + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
        const match = year.match(/\d+/);
        const yearNum = match ? parseInt(match[0]) : 4;
        const calculatedIndex = yearNum - 4;
        // Asegurar que nunca sea NaN y usar fallback con index
        return isNaN(calculatedIndex) ? index : calculatedIndex;
      });

      // Estado masivo de "No evaluada" para la habilidad seleccionada
      const allStudentsNotEvaluated =
        filteredStudents.length > 0 &&
        filteredStudents.every(student =>
          isSkillNotEvaluated(student, autoconcienciaVoluntadDimension.id, skill.id)
        );

      const toggleNotEvaluatedForAll = () => {
        if (!handleNotEvaluatedChange) return;
        const nextState = !allStudentsNotEvaluated;
        filteredStudents.forEach(student => {
          handleNotEvaluatedChange(
            student.id,
            autoconcienciaVoluntadDimension.id,
            selectedSkillId,
            nextState
          );
        });
      };

      return (
        <div className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              {/* Indicador de progreso */}
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de la habilidad:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        // Calcular porcentaje de estudiantes que han completado esta habilidad
                        const totalStudents = filteredStudents.length;
                        const completedStudents = filteredStudents.filter(student => 
                          getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id) !== null
                        ).length;
                        const progressPercent = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {filteredStudents.filter(student => 
                        getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id) !== null
                      ).length} de {filteredStudents.length} estudiantes
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("¿Seguro que desea reiniciar todos los niveles de esta habilidad para todos los estudiantes?")) {
                          filteredStudents.forEach(student => {
                            handleLocalLevelChange(student.id as any, skill.id, -1);
                          });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Reiniciar todo
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                        <div className="flex flex-col items-start gap-1">
                          <span>Estudiante</span>
                          {handleNotEvaluatedChange && (
                            <button
                              onClick={toggleNotEvaluatedForAll}
                              className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                                allStudentsNotEvaluated
                                  ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                  : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                              }`}
                              title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                            >
                              {allStudentsNotEvaluated
                                ? "✓ Habilitar evaluación"
                                : "🚫 No evaluar esta habilidad"}
                            </button>
                          )}
                        </div>
                      </th>
                  {visibleYears.map((ageGroup, index) => {
                    const actualIndex = visibleIndices[index]; // Obtener el índice real del nivel
                    return (
                      <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="mb-1">{ageGroup}</span>
                          <button
                            onClick={() => {
                              // Seleccionar este nivel para todos los estudiantes de la habilidad actual
                              if (selectedSkillId !== null) {
                                filteredStudents.forEach(student => {
                                  const currentLevel = getSkillLevel(student, autoconcienciaVoluntadDimension.id, selectedSkillId);
                                    if (currentLevel === actualIndex) {
                                      handleLevelChange(student.id as any, autoconcienciaVoluntadDimension.id, selectedSkillId, null);
                                    } else {
                                      handleLevelChange(student.id as any, autoconcienciaVoluntadDimension.id, selectedSkillId, actualIndex);
                                    }
                                });
                              }
                            }}
                            className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                          >
                            Seleccionar todo
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudents.map((student, studentIndex) => {
                    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

                    const expectedLevel = getExpectedLevel(age);
                    
                    const previousYearLevel = getHistoricalLevel(student, skill.id, age);

                    const selectedLevel = getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id);
                      const skillIsNotEvaluated = isSkillNotEvaluated(student, autoconcienciaVoluntadDimension.id, skill.id);
                    
                    return (
                          <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                            <td className="py-3 px-6 text-sm font-medium text-gray-900">
                              <div className="flex items-start">
                                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                                  <span className="text-indigo-800 font-bold text-xs">{student.name.split(' ')[0][0]}{student.name.split(' ')[1]?.[0]}</span>
                                </div>
                                <div className="ml-1 flex-1">
                                  <div className="font-medium">{student.name}</div>
                                  <div className="text-xs text-gray-500">{student.course}</div>
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, autoconcienciaVoluntadDimension.id, skill.id, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                                </div>
                              </div>
                            </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null; // Si no existe el nivel para este índice, no mostrar nada
                            
                            // Determinar si este nivel "no aplica" basado en el texto
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            const isExpected = levelIndex === expectedLevel;
                            const isPrevious = levelIndex === previousYearLevel;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              // Si es "No aplica", verificar si está seleccionado
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              // Mostrar nivel previamente seleccionado
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (isPrevious) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (isExpected) {
                              // Si es esperado, aplicar borde amarillo
                              if (!isPrevious) {
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }

                            return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                                  className={buttonClass}
                                  style={extraStyles}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                                >
                                  <span className="line-clamp-4">{level}</span>
                                </button>
                              </td>
                            );
                          })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
              </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada estudiante para esta habilidad.
                    </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                    </div>
                    </div>
                    </div>
                  </div>
      </div>
    );
    };
  }, [selectedSkillId, filteredStudents, getSkillLevel, handleLocalLevelChange, getExpectedLevel, getPreviousYearLevel, previouslySelectedLevels, showAllYears]);

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);

  const renderVisualizationView = useMemo(() => {
    return () => (
      <div>
        {selectedStudentIds.map(studentId => {
          const student = students.find(s => s.id === studentId);
          if (!student) return null;
          
          // Calcular la edad ancla por curso
          const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

          // Preparar los datos usando getSkillLevel
          const chartData = autoconcienciaVoluntadDimension.skills.map(skill => ({
            name: skill.name,
            percentageAchieved: getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id) === null 
              ? 0 
              : (getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id)! + 1) * 20,
            expectedLevel: calculateExpectedLevel(age),
            previousYearLevel: calculateLastYearPercentage(age)
          }));

          const yAxisWidth = 200;
          const barSize = 40;
          const minChartHeight = 200;
          const heightPerItem = 60;
          const chartHeight = Math.max(minChartHeight, chartData.length * heightPerItem);

          const wrapText = (text: string, maxWidth: number) => {
            const words = text.split(' ');
            const lines = [];
            let currentLine = words[0];

            for (let i = 1; i < words.length; i++) {
              const word = words[i];
              const width = getTextWidth(currentLine + " " + word);
              if (width < maxWidth) {
                currentLine += " " + word;
              } else {
                lines.push(currentLine);
                currentLine = word;
              }
            }
            lines.push(currentLine);
            return lines;
          };

          const getTextWidth = (text: string) => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (context) {
              context.font = '14px Arial';
              return context.measureText(text).width;
            }
            return 0;
          };

          return (
            <div key={student.id} className="mb-8">
              <h2 className="text-2xl font-bold mb-4 bg-gray-800 text-white p-3 rounded">
                {student.name} - {student.course}
              </h2>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-4">{autoconcienciaVoluntadDimension.name}</h3>
                <div className="w-full" style={{ height: `${chartHeight}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      layout="vertical"
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis 
                        type="number" 
                        domain={[0, 110]}
                        ticks={[20, 60, 100]}
                        tickFormatter={(value) => {
                          const ageMap: { [key: number]: string } = {
                            20: 'Hacia los 4 años',
                            60: 'Hacia los 6 años',
                            100: 'Hacia los 8 años'
                          };
                          return ageMap[value] || '';
                        }}
                        tick={{ fontSize: 14, fontWeight: 'bold' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={yAxisWidth}
                        tick={({ x, y, payload }) => {
                          const lines = wrapText(payload.value, yAxisWidth - 20);
                          return (
                            <g transform={`translate(${x},${y})`}>
                              {lines.map((line, index) => (
                                <text
                                  key={`item-${index}`}
                                  x={-10}
                                  y={index * 16}
                                  dy={-8 * (lines.length - 1)}
                                  textAnchor="end"
                                  fill="#333"
                                  fontSize={14}
                                  fontWeight="bold"
                                >
                                  {line}
                                </text>
                              ))}
                            </g>
                          );
                        }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <ZAxis range={[200, 200]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="percentageAchieved" 
                        fill={COLORS.achieved} 
                        name="Nivel habilidad logrado" 
                        barSize={barSize}
                      />
                      <Bar
                        dataKey="expectedLevel"
                        fill={COLORS.expected}
                        name="Nivel esperado"
                        barSize={4}
                        shape={<CustomBar />}
                      />
                      <Scatter 
                        dataKey="previousYearLevel" 
                        fill={COLORS.previousYear} 
                        name="Nivel año anterior" 
                        shape={<CustomVerticalRectangle />}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <DimensionSummary student={student} dimensionId={autoconcienciaVoluntadDimension.id} />
            </div>
          );
        })}
      </div>
    );
  }, [selectedStudentIds, students, DimensionSummary]);

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda en el centro */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
              No se encontraron estudiantes con "{searchTerm}"
            </div>
          ) : (
              filteredStudents.map((student) => {
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const progress = calculateFormProgress(
                  autoconcienciaVoluntadDimension.id,
                  student.course,
                  student.skillLevels
                );
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                <button
                  key={student.id}
                  onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                  {student.name}
            </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                  </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                  Tip: Ctrl + clic para seleccionar múltiples estudiantes
                </span>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar habilidad..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'course' | 'lastName')}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Número</option>
                  <option value="course">% Completado</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {autoconcienciaVoluntadDimension.skills
              .filter(skill => 
                searchTerm === '' || 
                skill.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((skill) => {
                const completed = filteredStudents.filter(student => 
                  getSkillLevel(student, autoconcienciaVoluntadDimension.id, skill.id) !== null
                ).length;
                const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
                
                return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, selectedSkillId === skill.id)
                }`}
              >
                    <div className="font-semibold" title={skill.name}>
                {skill.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
              </button>
                );
              })}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Selecciona una habilidad para ver detalles
                </span>
            </div>
          </div>
        </div>
      );
    }
  };

  const getLevelButtonClass = (student: Student, skillId: number, levelIndex: number) => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === autoconcienciaVoluntadDimension.id && sl.skillId === skillId
    );
    
    // Calcular la edad ancla por curso
    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
    
    const expectedLevel = getExpectedLevel(age);
    const previousYearLevel = getPreviousYearLevel(age);
    
    let classes = 'px-4 py-2 rounded border ';
    
    // Si es el nivel seleccionado
    if (skillLevel?.level === levelIndex) {
      classes += `${LEVEL_COLORS.selected} text-white `;
    } 
    // Si es el nivel esperado
    else if (levelIndex === expectedLevel) {
      classes += `${LEVEL_COLORS.expected} `;
    }
    // Si es el nivel del año anterior
    else if (levelIndex === previousYearLevel) {
      classes += `${LEVEL_COLORS.previous} `;
    }
    // Nivel no seleccionado
    else {
      classes += 'bg-white hover:bg-gray-100 ';
    }
    
    return classes;
  };

  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={autoconcienciaVoluntadDimension.name}
        icon={FaBrain}
        skillsCount={autoconcienciaVoluntadDimension.skills.length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
        
      {/* Botones de modo de vista */}
      <div className="mt-3">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
          <div className="grid grid-cols-2 gap-1">
                        <button
              onClick={() => handleViewModeChange('student')}
              className={`view-toggle-student flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'student' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-1.5 text-sm" />
              <span className="font-medium">Por Estudiante</span>
            </button>

            <button
              onClick={() => handleViewModeChange('skill')}
              className={`view-toggle-skill flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'skill' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaList className="mr-1.5 text-sm" />
              <span className="font-medium">Por Habilidad</span>
            </button>
          </div>
        </div>
      </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2 shadow-sm border"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel seleccionado</span> - Nivel evaluado para el estudiante
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel esperado</span> - Nivel correspondiente a la edad
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel año anterior</span> - Nivel del año previo
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Previamente seleccionado</span> - Nivel anterior guardado
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2 hover:bg-blue-50"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Sin seleccionar</span> - Nivel disponible para evaluar
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 border flex items-center justify-center">
                  <span className="text-xs text-gray-400 italic">N/A</span>
                </div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">No aplica</span> - No corresponde para esta edad
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-500 italic">
                💡 <strong>Tip:</strong> Los colores ayudan a identificar rápidamente el progreso de cada estudiante según su edad y desarrollo esperado.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const calculatePercentageAchieved = (dimension: { skills: Skill[] }) => {
  const totalLevels = dimension.skills.reduce((sum, skill) => sum + skill.levels.length, 0);
  const achievedLevels = dimension.skills.reduce((sum, skill) => {
    return sum + (skill.selectedLevelIndex === null ? 0 : skill.selectedLevelIndex + 1);
  }, 0);
  return (achievedLevels / totalLevels) * 100;
};

// Usar funciones centralizadas

const calculateExpectedLevel = (age: number) => {
  return calculateExpectedPercentage(age);
};

const calculateLastYearPercentage = (age: number) => {
  return calculatePreviousYearPercentage(age);
};

export default AutoconcienciaVoluntad;